import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import PlaceCard from './PlaceCard';
import FreeSpaceCard from './FreeSpaceCard';
import NavbarBreadcrumbs from '../../template/Dashboard/components/NavbarBreadcrumbs';
import { PlaceService } from '../../services/place/PlaceService';
import { CityService } from '../../services/city/CityService';
import { RegionService } from '../../services/region/RegionService';
import { DistrictService } from '../../services/district/DistrictService';
import { EventService } from '../../services/event/EventService';
import { EstabilishmentService } from '../../services/estabilishment/EstabilishmentService';

const PlaceCardList: React.FC<{
  searchParams: any;
  onUpdateModalData?: (espacos: any[], filterNames: any) => void;
}> = ({ searchParams, onUpdateModalData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const [espacos, setEspacos] = React.useState<any[]>([]);
  const [espacosGratuitos, setEspacosGratuitos] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingFree, setLoadingFree] = React.useState(true);
  const [isException, setIsException] = useState(false);
  const [filterNames, setFilterNames] = React.useState({
    cidade: '',
    regiao: '',
    bairro: '',
    evento: '',
    espaco: '',
  });

  React.useEffect(() => {
    setLoading(true);
    setLoadingFree(true);

    // Buscar espaços pagos
    PlaceService.search({
      fantasyName: searchParams.get('fantasyName') || null,
      districtId: searchParams.get('bairro') || null,
      cityId: searchParams.get('cidade') || null,
      regionId: searchParams.get('regiao') || null,
      placeEstablishmentId: searchParams.get('espaco') || null,
      placeEventTypeId: searchParams.get('evento') || null,
      invites: Number(searchParams.get('convidados')),
      hasAirConditioner: searchParams.get('ar') || null,
      hasBuffet: searchParams.get('buffet') || null,
      hasParking: searchParams.get('estacionamento') || null,
      hasOutdoor: searchParams.get('aoArLivre') || null,
      hasSound: searchParams.get('som') || null,
      hasRoom: searchParams.get('salas') || null,
    })
      .then((data) => {
        console.log('Espaços recebidos:', data);
        setIsException(data?.some((item: any) => item.exception === true));
        setEspacos(data || []);
      })
      .catch((error) => {
        console.error('Erro ao buscar espaços:', error);
        setEspacos([]);
      })
      .finally(() => {
        setPage(1);
        setLoading(false);
      });

    // Buscar espaços gratuitos
    PlaceService.search({
      fantasyName: searchParams.get('fantasyName') || null,
      districtId: searchParams.get('bairro') || null,
      cityId: searchParams.get('cidade') || null,
      regionId: searchParams.get('regiao') || null,
      placeEstablishmentId: searchParams.get('espaco') || null,
      placeEventTypeId: searchParams.get('evento') || null,
      invites: Number(searchParams.get('convidados')),
      hasAirConditioner: searchParams.get('ar') || null,
      hasBuffet: searchParams.get('buffet') || null,
      hasParking: searchParams.get('estacionamento') || null,
      hasOutdoor: searchParams.get('aoArLivre') || null,
      hasSound: searchParams.get('som') || null,
      hasRoom: searchParams.get('salas') || null,
      onlyFree: true,
    })
      .then((data) => {
        console.log('Espaços gratuitos recebidos:', data);
        setEspacosGratuitos(data || []);
      })
      .catch((error) => {
        console.error('Erro ao buscar espaços gratuitos:', error);
        setEspacosGratuitos([]);
      })
      .finally(() => {
        setLoadingFree(false);
      });
  }, [searchParams]);

  // Buscar nomes dos filtros baseados nos IDs dos searchParams
  React.useEffect(() => {
    const fetchFilterNames = async () => {
      const newFilterNames: any = {
        cidade: '',
        regiao: '',
        bairro: '',
        evento: '',
        espaco: '',
      };

      try {
        // Buscar nome da cidade
        const cidadeId = searchParams.get('cidade');
        if (cidadeId) {
          const citiesResponse = await CityService.search();
          const city = citiesResponse.data.find((c: any) => c.id === parseInt(cidadeId));
          if (city) newFilterNames.cidade = city.name;
        }

        // Buscar nome da região
        const regiaoId = searchParams.get('regiao');
        if (regiaoId && cidadeId) {
          const regionsResponse = await RegionService.searchByCity(cidadeId);
          const region = regionsResponse.data.find((r: any) => r.id === parseInt(regiaoId));
          if (region) newFilterNames.regiao = region.name;
        }

        // Buscar nome do bairro
        const bairroId = searchParams.get('bairro');
        if (bairroId && regiaoId) {
          const districtsResponse = await DistrictService.searchByRegion(regiaoId);
          const district = districtsResponse.data.find((d: any) => d.id === parseInt(bairroId));
          if (district) newFilterNames.bairro = district.name;
        }

        // Buscar nome do evento
        const eventoId = searchParams.get('evento');
        if (eventoId) {
          const eventsResponse = await EventService.search();
          const event = eventsResponse.data.find((e: any) => e.id === parseInt(eventoId));
          if (event) newFilterNames.evento = event.name;
        }

        // Buscar nome do estabelecimento
        const espacoId = searchParams.get('espaco');
        if (espacoId) {
          const establishmentsResponse = await EstabilishmentService.search();
          const establishment = establishmentsResponse.data.find(
            (e: any) => e.id === parseInt(espacoId),
          );
          if (establishment) newFilterNames.espaco = establishment.name;
        }

        setFilterNames(newFilterNames);
      } catch (error) {
        console.error('Erro ao buscar nomes dos filtros:', error);
      }
    };

    fetchFilterNames();
  }, [searchParams]);

  React.useEffect(() => {
    if (onUpdateModalData) {
      onUpdateModalData([...espacos, ...espacosGratuitos], filterNames);
    }
  }, [espacos, espacosGratuitos, filterNames, isException]);

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  if (!loading && espacos.length === 0) {
    return (
      <Box p={isMobile ? 1.5 : 3}>
        <Paper sx={{ width: '100%', textAlign: 'center', p: isMobile ? 2 : 4 }}>
          <Typography
            variant={isSmallMobile ? 'body2' : 'h6'}
            sx={{ color: theme.palette.primary.main }}
          >
            Nenhum espaço encontrado.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const paginatedData = espacos.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const pageCount = Math.ceil(espacos.length / itemsPerPage);
  const exactMatches = espacos.filter((e: any) => !e.exception).length;

  return loading ? (
    <Box sx={{ width: '100%', p: isMobile ? 2 : 10 }}>
      <Skeleton />
      <Skeleton animation="wave" />
      <Skeleton animation={false} />
    </Box>
  ) : (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isSmallMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          gap: isSmallMobile ? 1 : 0,
          pr: isMobile ? 1.5 : 3,
          pl: isMobile ? 1.5 : 3,
        }}
      >
        <NavbarBreadcrumbs searchParams={searchParams} filterNames={filterNames} />
        <Typography
          variant={isSmallMobile ? 'caption' : isMobile ? 'body2' : 'h6'}
          sx={{
            mb: isMobile ? 1 : 2,
            textAlign: isSmallMobile ? 'left' : 'right',
            whiteSpace: 'nowrap',
          }}
        >
          {isException ? (
            <>
              {exactMatches} <b>Resultados</b> • {espacos.length - exactMatches} <b>Sugestões</b>
            </>
          ) : (
            <>
              {espacos.length} <b>Resultados</b>
            </>
          )}
        </Typography>
      </Box>
      {isException && (
        <Paper elevation={2} sx={{ m: isMobile ? 1 : 2, p: isMobile ? 1.5 : 2 }}>
          <Typography
            variant={isSmallMobile ? 'body2' : 'h5'}
            fontWeight="bold"
            sx={{ color: theme.palette.primary.main }}
          >
            Desculpe! Não encontramos espaços com esse filtro, mas temos algumas sugestões para você
          </Typography>
          <Typography
            my={isMobile ? 1 : 2}
            sx={{ fontSize: isSmallMobile ? '0.75rem' : 'inherit' }}
          >
            Ajuste os filtros para refinar sua busca ou explore outras opções de espaços que podem
            ser do seu interesse:
          </Typography>
        </Paper>
      )}
      <Box display="flex" alignItems="center" justifyContent="flex-end" p={isMobile ? 1 : 2}>
        <FormControl variant="standard" size={isSmallMobile ? 'small' : 'medium'}>
          <InputLabel
            id="items-per-page-label"
            sx={{ fontSize: isSmallMobile ? '0.75rem' : 'inherit' }}
          >
            Itens por página
          </InputLabel>
          <Select
            labelId="items-per-page-label"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            label="Itens por página"
            sx={{
              width: isSmallMobile ? 80 : 100,
              fontSize: isSmallMobile ? '0.75rem' : 'inherit',
            }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box
        p={isMobile ? 1 : 2}
        sx={{
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
          {paginatedData.map((espaco: EspacoDto) => (
            <PlaceCard key={espaco.id} {...espaco} />
          ))}
        </Box>
      </Box>

      {/* Paginação */}
      {pageCount > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: isMobile ? 2 : 4,
            mb: isMobile ? 2 : 0,
          }}
        >
          <Pagination
            count={pageCount}
            page={page}
            onChange={handleChange}
            color="primary"
            size={isSmallMobile ? 'small' : 'medium'}
          />
        </Box>
      )}

      {/* Espaços Gratuitos - Estilo Classificados */}
      {!loadingFree && espacosGratuitos.length > 0 && (
        <Box sx={{ mt: isMobile ? 3 : 4, px: isMobile ? 1 : 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 1.5 : 2,
              mb: isMobile ? 1.5 : 2,
              my: 5,
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            }}
          >
            <Typography variant={isSmallMobile ? 'subtitle2' : 'h6'} fontWeight="bold">
              Verifique também outros espaços disponíveis:
            </Typography>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))',
              gap: isMobile ? 1 : 1.5,
            }}
            mb={5}
          >
            {espacosGratuitos.map((espaco: EspacoDto) => (
              <FreeSpaceCard key={espaco.id} {...espaco} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PlaceCardList;
